import { useMemo, useRef, useSyncExternalStore } from 'react';
import { shallowEqual } from '@ayocore/platform/client/utils/shallowEqual';

type SelectorController<TState extends object> = {
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => TState;
};

/**
 * Подписка на внешний store контроллера с селектором и shallow-мемоизацией.
 * Избегает лишних ререндеров при неизменившемся выбранном срезе.
 *
 * Поднят из CRM Activity (`components/Activity/hooks/useControllerSelector.ts`,
 * там остался реэкспорт) как общий примитив платформы.
 */
export function useControllerSelector<TState extends object, TSelected>(
  controller: SelectorController<TState>,
  selector: (state: TState) => TSelected
): TSelected {
  const state = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot,
    controller.getSnapshot
  );

  const selectedRef = useRef<TSelected | undefined>(undefined);
  return useMemo(() => {
    const selected = selector(state);
    if (selectedRef.current !== undefined) {
      if (shallowEqual(selectedRef.current, selected)) {
        return selectedRef.current as TSelected;
      }
    }
    selectedRef.current = selected;
    return selected;
  }, [state, selector]);
}
